<script lang="ts">
  import { onMount } from 'svelte';
  import AppShell from '../components/AppShell.svelte';
  import { apiFetch } from '../lib/api';
  import { fmtInt, fmtUah } from '../lib/format';

  interface RetMatrix {
    weekOffsets: number[];
    rows: { cohortLabel: string; cohortDate: string; size: number; values: (number | null)[] }[];
  }
  interface LtvMatrix {
    monthOffsets: number[];
    rows: { cohortLabel: string; cohortDate: string; size: number; values: (number | null)[] }[];
  }

  let retention = $state<RetMatrix | null>(null);
  let ltv = $state<LtvMatrix | null>(null);
  let loading = $state(true);
  let err = $state<string | null>(null);

  onMount(async () => {
    try {
      const [r, l] = await Promise.all([
        apiFetch<RetMatrix>('/kpi/cohorts/retention?weeks=12'),
        apiFetch<LtvMatrix>('/kpi/cohorts/ltv?months=6'),
      ]);
      retention = r;
      ltv = l;
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  });

  function cellClass(v: number | null): string {
    if (v === null) return 'bg-[var(--color-bg-2)] text-[var(--color-text-muted)]';
    if (v < 20) return 'bg-purple-50';
    if (v < 40) return 'bg-purple-200';
    if (v < 60) return 'bg-purple-400 text-white';
    if (v < 80) return 'bg-purple-600 text-white';
    return 'bg-purple-800 text-white';
  }
</script>

<AppShell active="cohorts">
  <div class="mx-auto max-w-[1200px]">
    <header class="mb-5">
      <h1 class="text-2xl font-bold leading-tight">Когорти, Retention, LTV</h1>
      <p class="mt-1 text-[12px] text-[var(--color-text-muted)]">
        Тижневі когорти за датою першого <code>fy.auth.login</code> · обчислюється on-demand з кешем 60 с
      </p>
    </header>

    {#if loading}
      <div class="py-20 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
    {:else if err}
      <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
    {:else}
      <!-- Retention heatmap -->
      {#if retention}
        <div class="rounded-xl border border-[var(--color-border-3)] bg-white p-4 mb-4">
          <div class="text-[13px] font-bold mb-1">Weekly retention heatmap</div>
          <div class="text-[10.5px] text-[var(--color-text-muted)] mb-3">
            % когорти, що повернулися (активна подія) на тижні X
          </div>
          {#if retention.rows.length === 0}
            <div class="py-8 text-center text-xs text-[var(--color-text-muted)]">Поки немає достатньо когорт</div>
          {:else}
            <div class="overflow-x-auto">
              <table class="min-w-[760px] border-separate" style="border-spacing: 2px">
                <thead>
                  <tr class="text-[10px] uppercase text-[var(--color-text-muted-2)]">
                    <th class="text-left py-1.5 pl-2 w-[120px]">Когорта</th>
                    <th class="text-right py-1.5 px-1">Size</th>
                    {#each retention.weekOffsets as w (w)}<th class="py-1.5 px-1">W{w}</th>{/each}
                  </tr>
                </thead>
                <tbody>
                  {#each retention.rows as r (r.cohortDate)}
                    <tr>
                      <td class="text-left pl-2 rounded bg-[var(--color-bg-3)] py-1.5 text-xs font-semibold">{r.cohortLabel}</td>
                      <td class="text-right rounded bg-[var(--color-bg-3)] py-1.5 px-1 text-xs text-[var(--color-text-muted)] tabular-nums">{fmtInt(r.size)}</td>
                      {#each r.values as v, i (i)}
                        <td class="text-center rounded py-1.5 px-1 text-[10.5px] font-bold tabular-nums {cellClass(v)}">
                          {v === null ? '—' : `${v}%`}
                        </td>
                      {/each}
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
            <div class="mt-3 flex items-center gap-3 text-[10.5px] text-[var(--color-text-muted)]">
              <span>Шкала:</span>
              <span class="rounded-full bg-purple-50 px-1.5 py-0.5 font-bold">0-20</span>
              <span class="rounded-full bg-purple-200 px-1.5 py-0.5 font-bold">20-40</span>
              <span class="rounded-full bg-purple-400 px-1.5 py-0.5 font-bold text-white">40-60</span>
              <span class="rounded-full bg-purple-600 px-1.5 py-0.5 font-bold text-white">60-80</span>
              <span class="rounded-full bg-purple-800 px-1.5 py-0.5 font-bold text-white">80-100</span>
              <span class="ml-auto">Σ когорт: {fmtInt(retention.rows.reduce((a, r) => a + r.size, 0))}</span>
            </div>
          {/if}
        </div>
      {/if}

      <!-- LTV by cohort -->
      {#if ltv}
        <div class="rounded-xl border border-[var(--color-border-3)] bg-white p-4">
          <div class="text-[13px] font-bold mb-1">LTV by cohort</div>
          <div class="text-[10.5px] text-[var(--color-text-muted)] mb-3">
            Накопичений ARPU на місяцях після входу (UAH)
          </div>
          {#if ltv.rows.length === 0}
            <div class="py-8 text-center text-xs text-[var(--color-text-muted)]">Поки немає достатньо даних</div>
          {:else}
            <table class="w-full text-xs">
              <thead>
                <tr class="text-[10px] uppercase text-[var(--color-text-muted-2)]">
                  <th class="text-left py-1.5">Когорта</th>
                  <th class="text-right py-1.5">Size</th>
                  {#each ltv.monthOffsets as m (m)}<th class="text-right py-1.5">M{m}</th>{/each}
                </tr>
              </thead>
              <tbody>
                {#each ltv.rows as r (r.cohortDate)}
                  <tr class="border-t border-[var(--color-border-3)]">
                    <td class="py-1.5 text-[var(--color-text-muted)]">{r.cohortLabel}</td>
                    <td class="text-right font-semibold tabular-nums">{fmtInt(r.size)}</td>
                    {#each r.values as v, i (i)}
                      <td class="text-right py-1.5 tabular-nums font-bold {v === null ? 'text-[var(--color-text-muted)]' : ''}">
                        {v === null ? '—' : fmtUah(v)}
                      </td>
                    {/each}
                  </tr>
                {/each}
              </tbody>
            </table>
          {/if}
        </div>
      {/if}
    {/if}
  </div>
</AppShell>
